import { Component, Injector, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import * as Highcharts from 'highcharts';
import { HC_COLORS } from '../../charts/highcharts-shared';
import { HighchartsChartComponent, providePartialHighcharts } from 'highcharts-angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from 'src/app/app.materials';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { PortfolioService } from 'src/app/pages/portfolio/portfolio.service';
import { ApiEndpoints } from 'src/app/ApiEndpoints';
import { InfoComponent } from '../info/info.component';

interface AssetImpact {
  ticker: string;
  name: string;
  old_weight: number;
  new_weight: number;
  delta: number;
}

interface RankingItem {
  ticker: string;
  name: string;
  risk_reduction_potential: number;
  current_weight: number;
  current_pf_risk: number;
  best_hedge_weight: number;
  best_hedge_pf_risk: number;
  asset_impacts?: AssetImpact[];
}

interface CurveData {
  asset_index: number;
  grid: number[];
  sigma: number[];
  current_weight: number;
  current_sigma: number;
  min_sigma: number;
  argmin_weight: number;
  risk_reduction_potential: number;
  derived_portfolio_weights: Record<string, number>;
  current_return: number;
  min_return: number;
  current_sharpe: number;
  min_sharpe: number;
}

interface PortfolioImpactRow {
  label: string;
  current: string;
  bestHedge: string;
  change: string;
  changeValue: number;
}

@Component({
  selector: 'app-best-hedge',
  templateUrl: './best-hedge.component.html',
  styleUrls: ['./best-hedge.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [MaterialModule, HighchartsChartComponent, CommonModule, FormsModule, InfoComponent],
  providers: [
    providePartialHighcharts({ modules: () => [] })
  ]
})
export class BestHedgeComponent implements OnInit, OnDestroy {

  chartOptions: Highcharts.Options = {};
  chartLoaded = false;
  isLoading = false;

  ignoreConstraints = false;

  rankingItems: RankingItem[] = [];
  curves: Record<string, CurveData> = {};
  selectedTicker: string = '';
  assetImpacts: AssetImpact[] = [];
  private nameMap: Record<string, string> = {};

  displayedColumns: string[] = ['name', 'currentWeight', 'currentPfRisk', 'bestHedgeWeight', 'bestHedgePfRisk', 'riskReductionPotential'];
  tableDataSource = new MatTableDataSource<RankingItem>([]);

  private chartInstance: Highcharts.Chart | null = null;
  private portfolioService: PortfolioService;
  private portfolioId: number | null = null;
  private portfolioSub: Subscription;

  constructor(private injector: Injector) {
    this.portfolioService = injector.get(PortfolioService);
  }

  ngOnInit(): void {
    this.portfolioSub = this.portfolioService.portfolio$.subscribe(portfolio => {
      if (portfolio?.id) {
        this.portfolioId = portfolio.id;
        if (portfolio.security_overviews) {
          this.nameMap = Object.entries(portfolio.security_overviews).reduce(
            (acc: Record<string, string>, [ticker, info]: [string, any]) => {
              acc[ticker] = info.company_name ?? info.name ?? ticker;
              return acc;
            },
            {}
          );
        }
        this.fetchData();
      }
    });
  }

  ngOnDestroy(): void {
    this.portfolioSub?.unsubscribe();
    this.chartInstance = null;
  }

  onChartInstance(chart: Highcharts.Chart): void {
    this.chartInstance = chart;
  }

  onIgnoreConstraintsChange(): void {
    this.fetchData();
  }

  fetchData(): void {
    if (!this.portfolioId) return;
    this.isLoading = true;
    this.chartLoaded = false;
    this.chartInstance = null;

    const endpoint = ApiEndpoints.portfolioBestHedge.replace(':id', String(this.portfolioId));
    this.portfolioService.getRequest<any>(endpoint, { ignore_constraints: this.ignoreConstraints })
      .subscribe({
        next: (resp: any) => {
          this.isLoading = false;
          const results = resp?.success?.results;
          if (results?.curves) {
            this.processResults(results);
          }
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  processResults(results: any): void {
    this.curves = results.curves ?? {};
    this.rankingItems = this.buildRanking();
    this.tableDataSource.data = this.rankingItems;

    const stillPresent = this.rankingItems.some(r => r.ticker === this.selectedTicker);
    this.selectedTicker = stillPresent ? this.selectedTicker : (this.rankingItems[0]?.ticker ?? '');

    if (this.selectedTicker) {
      this.buildChart(this.selectedTicker);
    }
  }

  buildRanking(): RankingItem[] {
    return Object.entries(this.curves)
      .map(([ticker, data]) => {
        const assetImpacts: AssetImpact[] = Object.entries(data.derived_portfolio_weights).map(
          ([impactTicker, newWeight]) => ({
            ticker: impactTicker,
            name: this.nameMap[impactTicker] ?? impactTicker,
            old_weight: this.curves[impactTicker]?.current_weight ?? 0,
            new_weight: newWeight,
            delta: newWeight - (this.curves[impactTicker]?.current_weight ?? 0),
          })
        );
        return {
          ticker,
          name: this.nameMap[ticker] ?? ticker,
          risk_reduction_potential: data.risk_reduction_potential,
          current_weight: data.current_weight,
          current_pf_risk: data.current_sigma,
          best_hedge_weight: data.argmin_weight,
          best_hedge_pf_risk: data.min_sigma,
          asset_impacts: assetImpacts,
        };
      })
      .sort((a, b) => b.risk_reduction_potential - a.risk_reduction_potential);
  }

  onAssetChange(ticker: string): void {
    this.selectedTicker = ticker;
    this.buildChart(ticker);
  }

  buildChart(ticker: string): void {
    const assetData = this.curves[ticker];
    if (!assetData) return;

    const assetInfo = this.rankingItems.find(i => i.ticker === ticker);
    const assetName = assetInfo ? assetInfo.name : ticker;
    this.assetImpacts = assetInfo?.asset_impacts ?? [];
    const curvePoints: [number, number][] = assetData.grid.map((x, i) => [x, assetData.sigma[i]]);

    // If the chart already exists, update its data in-place to avoid
    // destroying/recreating the DOM (which causes flicker and the
    // stock-module "Cannot read properties of undefined" crash).
    if (this.chartInstance && this.chartLoaded) {
      this.chartInstance.series[0]?.setData(curvePoints, false);
      this.chartInstance.series[1]?.setData(
        [[assetData.current_weight, assetData.current_sigma]], false
      );
      this.chartInstance.series[2]?.setData(
        [[assetData.argmin_weight, assetData.min_sigma]], false
      );
      this.chartInstance.setTitle(
        { text: 'Risk Sensitivity Curve — ' + assetName },
        { text: 'How adjusting this asset\'s weight affects total portfolio volatility' },
        false
      );
      this.chartInstance.redraw(true);
      return;
    }

    this.chartOptions = {
      chart: {
        type: 'spline',
        height: 420,
        backgroundColor: 'transparent',
        plotBackgroundColor: 'transparent'
      },
      credits: { enabled: false },
      navigator: { enabled: false },
      scrollbar: { enabled: false },
      rangeSelector: { enabled: false },
      title: {
        text: 'Risk Sensitivity Curve — ' + assetName
      },
      subtitle: {
        text: 'How adjusting this asset\'s weight affects total portfolio volatility'
      },
      xAxis: {
        title: { text: 'Weight in Portfolio', style: { color: 'var(--text-dim)' } },
        labels: {
          style: { color: 'var(--text-dim)', fontSize: '12px' },
          formatter: function () {
            return (this.value as number * 100).toFixed(0) + '%';
          }
        }
      },
      yAxis: {
        title: { text: 'Total Portfolio Volatility (Sigma)', style: { color: 'var(--text-dim)' } },
        labels: {
          style: { color: 'var(--text-dim)', fontSize: '12px' },
          formatter: function () {
            return (this.value as number * 100).toFixed(1) + '%';
          }
        }
      },
      tooltip: {
        headerFormat: '<b>{series.name}</b><br>',
        pointFormatter: function () {
          return `Weight: <b>${(this.x * 100).toFixed(1)}%</b><br>Volatility: <b>${(this.y * 100).toFixed(2)}%</b>`;
        }
      },
      legend: { enabled: true },
      series: [
        {
          type: 'spline',
          name: 'Risk Curve',
          data: curvePoints,
          color: HC_COLORS.accent,
          marker: { enabled: false },
          lineWidth: 3
        },
        {
          type: 'scatter',
          name: 'Current Portfolio State',
          data: [[assetData.current_weight, assetData.current_sigma]],
          marker: {
            symbol: 'cross',
            lineColor: HC_COLORS.neg,
            lineWidth: 3,
            radius: 8,
            fillColor: 'transparent'
          }
        },
        {
          type: 'scatter',
          name: 'Minimum Risk State',
          data: [[assetData.argmin_weight, assetData.min_sigma]],
          marker: {
            symbol: 'circle',
            fillColor: HC_COLORS.pos,
            radius: 7
          }
        }
      ]
    };
    this.chartLoaded = true;
  }

  getRiskClass(value: number): string {
    const pct = value * 100;
    if (pct > 0.01) return 'risk-positive';
    if (pct < -0.01) return 'risk-negative';
    return 'risk-neutral';
  }

  formatPct(value: number | null | undefined, decimals = 2): string {
    if (value === null || value === undefined || !Number.isFinite(value)) return 'N/A';
    return (value * 100).toFixed(decimals) + '%';
  }

  formatNumber(value: number | null | undefined, decimals = 2): string {
    if (value === null || value === undefined || !Number.isFinite(value)) return 'N/A';
    return value.toFixed(decimals);
  }

  get portfolioImpactRows(): PortfolioImpactRow[] {
    const curve = this.curves[this.selectedTicker];
    if (!curve) return [];

    const diff = (a: number | null | undefined, b: number | null | undefined): number | null => {
      if (a === null || a === undefined || !Number.isFinite(a)) return null;
      if (b === null || b === undefined || !Number.isFinite(b)) return null;
      return a - b;
    };

    const formatChange = (
      change: number | null,
      formatter: (v: number | null | undefined) => string
    ): string => {
      if (change === null) return 'N/A';
      return (change >= 0 ? '+' : '') + formatter(change);
    };

    const returnChange = diff(curve.min_return, curve.current_return);
    const riskChange = diff(curve.min_sigma, curve.current_sigma);
    const sharpeChange = diff(curve.min_sharpe, curve.current_sharpe);

    return [
      {
        label: 'Return',
        current: this.formatPct(curve.current_return),
        bestHedge: this.formatPct(curve.min_return),
        change: formatChange(returnChange, v => this.formatPct(v)),
        changeValue: returnChange ?? 0,
      },
      {
        label: 'Risk',
        current: this.formatPct(curve.current_sigma),
        bestHedge: this.formatPct(curve.min_sigma),
        change: formatChange(riskChange, v => this.formatPct(v)),
        changeValue: riskChange ?? 0,
      },
      {
        label: 'Sharpe',
        current: this.formatNumber(curve.current_sharpe),
        bestHedge: this.formatNumber(curve.min_sharpe),
        change: formatChange(sharpeChange, v => this.formatNumber(v)),
        changeValue: sharpeChange ?? 0,
      },
    ];
  }
}
